/**
 * キーボードガイドに重ねる半透明の手(docs/02_game_design.md §3「指の透過表示」)。
 *
 * 参考画像(修正・参考/タイピング画面参考.png)の輪郭線の手を SVG で自作したもの。
 *  - ホームポジションに指先を置いた左右の手を、キーが透けて見える半透明で重ねる
 *  - 次に打つキーを担当する指だけ、そのキーまで指先を伸ばして指の色で強調する
 *
 * 形の計算は `logic/hands.ts`(純関数)。ここは描画だけ。
 * キーボードSVGと同じ viewBox・同じ箱に置くので、指先が必ずキーの中心に合う。
 */

import { useMemo } from 'react';
import { FINGER_LABELS, FINGER_COLORS, highlightedFingers } from '../../logic/keyboard';
import { LAYOUT_VIEW_BOX } from '../../logic/keyboardLayout';
import { fingerPath, fingerTargets, fingerTip, handPath, HAND_SPECS } from '../../logic/hands';

interface Props {
  readonly nextKeys: readonly string[];
}

/** 手の輪郭の色(参考画像のオレンジの線)。 */
const OUTLINE = '#f2a65a';
/** 手の内側の色。キーが透けるよう薄く塗る。 */
const FILL = '#fff6df';

export function HandsOverlay({ nextKeys }: Props) {
  const active = useMemo(() => highlightedFingers(nextKeys), [nextKeys]);
  const targets = useMemo(() => fingerTargets(nextKeys), [nextKeys]);
  const names = useMemo(
    () => [...active].map((finger) => FINGER_LABELS[finger]).join(' / '),
    [active],
  );

  return (
    <svg
      className="hands-overlay"
      viewBox={LAYOUT_VIEW_BOX}
      role="img"
      aria-label={names ? `ゆびガイド: ${names}` : 'ゆびガイド'}
    >
      {HAND_SPECS.map((hand) => (
        <g key={hand.side} className={`hand hand-${hand.side}`}>
          {/* 手のひら+指をまとめた1本のパス。重なっても塗りが濃くならない。 */}
          <path
            d={handPath(hand, targets)}
            fill={FILL}
            fillOpacity={0.3}
            fillRule="nonzero"
            stroke={OUTLINE}
            strokeOpacity={0.85}
            strokeWidth={3}
            strokeLinejoin="round"
          />
          {/* 次に打つ指だけ、上から色付きの輪郭でなぞる。 */}
          {hand.fingers
            .filter((finger) => active.has(finger.id))
            .map((finger) => (
              <path
                key={`${hand.side}-${finger.id}`}
                className="finger on"
                d={fingerPath(
                  finger.base,
                  fingerTip(finger, targets),
                  finger.baseHalf,
                  finger.tipHalf,
                )}
                fill={FINGER_COLORS[finger.id]}
                fillOpacity={0.22}
                stroke={FINGER_COLORS[finger.id]}
                strokeWidth={4}
                strokeLinejoin="round"
              />
            ))}
        </g>
      ))}
    </svg>
  );
}
